/**
 * Error boundary for CCMS SDK errors
 * Catches and displays SDK errors gracefully
 */

import React, { Component, ReactNode } from 'react';
import { Card, Stack, Text, Title, Button, Group } from '@/components/ui';
import { IconAlertCircle, IconRefresh } from '@tabler/icons-react';
import { getErrorMessage, formatErrorForUser } from '../../utils/error-handling';
import { usePreferencesStore } from '../../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../hooks/useAccessibilityStyles';

interface DefaultErrorUIProps {
  userMessage: string;
  technicalMessage: string | null;
  showDetails: boolean;
  onReset: () => void;
}

/**
 * Functional component for the default error UI to enable hooks usage
 */
function DefaultErrorUI({ userMessage, technicalMessage, showDetails, onReset }: DefaultErrorUIProps) {
  const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
  const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

  const accessibilityStyles = {
    lineHeight: LINE_HEIGHT_MAP[lineSpacing],
    letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
  };

  return (
    <Card
      variant="elevated"
      style={{
        maxWidth: 600,
        margin: '2rem auto',
        padding: '2rem',
      }}
    >
      <Stack gap="md">
        <Group>
          <IconAlertCircle size={32} color="var(--mantine-color-red-6)" />
          <Title order={3}>Something went wrong</Title>
        </Group>

        <Text size="md" c="dimmed" style={accessibilityStyles}>
          {userMessage}
        </Text>

        {technicalMessage && showDetails && (
          <Card variant="subtle" p="sm">
            <Text size="sm" c="dimmed" style={{ fontFamily: 'monospace', ...accessibilityStyles }}>
              {technicalMessage}
            </Text>
          </Card>
        )}

        <Group>
          <Button
            variant="gradient"
            gradientVariant="primary"
            leftSection={<IconRefresh size={16} />}
            onClick={onReset}
          >
            Try Again
          </Button>

          {process.env.NODE_ENV === 'development' && (
            <Button
              variant="light"
              onClick={() => console.error('Error details:', userMessage)}
            >
              Log to Console
            </Button>
          )}
        </Group>
      </Stack>
    </Card>
  );
}

interface CCMSErrorBoundaryProps {
  children: ReactNode;
  fallback?: (error: Error, reset: () => void) => ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  showDetails?: boolean;
}

interface CCMSErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error boundary component for catching SDK and component errors
 *
 * @example
 * ```tsx
 * <CCMSErrorBoundary>
 *   <FileUploader domain="User" />
 * </CCMSErrorBoundary>
 * ```
 *
 * @example With custom fallback
 * ```tsx
 * <CCMSErrorBoundary
 *   fallback={(error, reset) => (
 *     <div>
 *       <p>Error: {error.message}</p>
 *       <button onClick={reset}>Try Again</button>
 *     </div>
 *   )}
 * >
 *   <MyComponent />
 * </CCMSErrorBoundary>
 * ```
 */
export class CCMSErrorBoundary extends Component<
  CCMSErrorBoundaryProps,
  CCMSErrorBoundaryState
> {
  constructor(props: CCMSErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): CCMSErrorBoundaryState {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('CCMSErrorBoundary caught an error:', error, errorInfo);
    }

    // Call onError callback if provided
    this.props.onError?.(error, errorInfo);
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
    });
  };

  render() {
    const { children, fallback, showDetails = false } = this.props;
    const { hasError, error } = this.state;

    if (hasError && error) {
      // Use custom fallback if provided
      if (fallback) {
        return fallback(error, this.handleReset);
      }

      // Default error UI
      const userMessage = formatErrorForUser(error);
      const technicalMessage = showDetails ? getErrorMessage(error) : null;

      return (
        <DefaultErrorUI
          userMessage={userMessage}
          technicalMessage={technicalMessage}
          showDetails={showDetails}
          onReset={this.handleReset}
        />
      );
    }

    return children;
  }
}

export default CCMSErrorBoundary;
